import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { generateRollout } from '../Services/GenerateRolloutService';
import { ErrorFallback, Loader, buildNotification, GenerateNotification } from 'smart-react';
import 'dropzone/dist/dropzone.css';
import './GenerateRollout.scss';
import { ErrorBoundary } from 'react-error-boundary';
import { EVENTS_DATA_TYPES, NOTIFICATION_TYPES } from '../../../constants/eventDataTypes';

const GenerateRollout = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('zip'); // Default to zip

  // Set up Dropzone configuration
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/zip': ['.zip'],
    },
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        GenerateNotification(
          buildNotification({
            title: 'Invalid File Type!',
            description: 'Only .zip files are allowed.',
            style: 'error',
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
      }
    },
  });

  const handleProcess = async () => {
    if (!file) return;

    setIsProcessing(true);

    try {
      const data = await generateRollout(file, fileType); // Send fileType along with file
      if (data.isSuccess) {
        setDownloadUrl(data.downloadUrl);
        setShowPopup(true);
        setFile(null);
        GenerateNotification(
          buildNotification({
            title: 'Rollout Successfully Generated!',
            style: 'success',
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification({
            title: 'Error Generating Rollout!',
            description: data.message,
            style: 'error',
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      }
    } catch (error) {
      console.error('Error processing file:', error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClosePopup = () => setShowPopup(false);

  const handleDownload = () => {
    setShowPopup(false);
    window.location.href = downloadUrl;
  };

  const handleDeleteFile = () => {
    setFile(null);
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <React.Suspense fallback={<Loader />}>
        {loading && <Loader />}
        <div className='container mx-auto rollout-form-container k-p-3'>
          <p className='upload-info'>
            Please upload a zip or tar file that includes the files for which you want to generate the rollout.
          </p>
          <div className='file-type-selector'>
            <label><strong>Export Type:</strong></label>
            <label>
              <input
                type='radio'
                value='zip'
                checked={fileType === 'zip'}
                onChange={() => setFileType('zip')}
              />
              ZIP
            </label>
            <label>
              <input
                type='radio'
                value='tar'
                checked={fileType === 'tar'}
                onChange={() => setFileType('tar')}
              />
              TAR
            </label>
          </div>
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <p>Drag 'n' drop a file here, or click to select one</p>
          </div>

          {file && (
            <div className='file-info'>
              <p><strong>Selected File:</strong></p>
              <p>Name: {file.name}</p>
              <p>Size: {(file.size / 1024 / 1024).toFixed(2)} MB</p>
              <Button
                themeColor={'secondary'}
                className='k-mx-1'
                onClick={handleDeleteFile}
              >
                Delete
              </Button>
            </div>
          )}

          <div className='process-button-container'>
            <Button
              themeColor={'primary'}
              onClick={handleProcess}
              disabled={isProcessing || !file}
            >
              Process
            </Button>
          </div>

          {isProcessing && <Loader />}

          {showPopup && (
            <Dialog title='Rollout Available' onClose={handleClosePopup}>
              <p>Please click the button below to download the Rollout.</p>
              <DialogActionsBar>
                <Button
                  themeColor={'primary'}
                  className='k-mx-1'
                  size={'large'}
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </DialogActionsBar>
            </Dialog>
          )}
        </div>
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default GenerateRollout;
